<template>
  <section class="mx-auto max-w-[1440px] space-y-4 px-3 py-4 sm:px-5 lg:px-6">
    <div v-if="loading" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-5 text-sm text-muted">
      Loading service vendor details...
    </div>
    <div v-else-if="error" class="rounded-[24px] border border-red-100 bg-red-50 p-5 text-sm font-semibold text-red-700">
      {{ error }}
    </div>

    <div v-else class="space-y-4">
      <section class="overflow-hidden rounded-[26px] border border-[#eadfd4] bg-white shadow-sm">
        <div class="border-b border-[#efe3d8] px-4 py-3 sm:px-5 lg:px-6">
          <nav class="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted sm:text-sm">
            <i class="ri-store-2-line text-base text-brand"></i>
            <span class="font-medium text-[#b78969]">Business</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="font-medium text-[#b78969]">{{ vendor.category || 'Service vendor' }}</span>
            <span class="text-[#d2b9a3]">/</span>
            <span class="min-w-0 truncate font-semibold text-ink">{{ vendor.title || 'Service vendor' }}</span>
          </nav>
        </div>

        <div class="space-y-3 px-4 py-4 sm:px-5 lg:px-6">
          <div class="mx-auto grid max-w-[1180px] grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4 lg:grid-rows-2">
            <button
              v-for="(image, index) in heroGallery"
              :key="`${image.id || image.src}-${index}`"
              type="button"
              class="relative aspect-[4/3] min-w-0 overflow-hidden rounded-[16px] text-left sm:rounded-[20px]"
              :class="index === 0 ? 'lg:col-span-2 lg:row-span-2 lg:h-[420px] lg:min-h-0 lg:rounded-[24px]' : 'lg:h-[204px] lg:min-h-0'"
              @click="openVendorGalleryById(image)"
            >
              <img
                :src="image.src"
                :alt="vendor.title"
                class="block h-full w-full rounded-[16px] bg-[#f5efe8] object-cover sm:rounded-[20px]"
                :class="index === 0 ? 'lg:rounded-[24px]' : ''"
              >
              <div v-if="image.overlayText" class="absolute inset-0 flex flex-col items-center justify-center bg-[#1f1208]/55 text-center text-white">
                <span class="text-[1.45rem] font-semibold leading-none">{{ image.overlayText }}</span>
                <span class="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.18em]">more photos</span>
              </div>
            </button>
          </div>

          <div class="border-b border-[#efe3d8] pb-3 pt-2">
            <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div class="min-w-0">
                <h1 class="flex flex-wrap items-center gap-2 text-[1.2rem] font-medium leading-tight text-[#4b5563] sm:text-[1.35rem] lg:text-[1.55rem]">
                  <span>{{ vendor.title || 'Service vendor' }}</span>
                  <i v-if="vendor.published" class="ri-checkbox-circle-fill text-[1.25rem] text-[#1683ff]"></i>
                </h1>
                <p v-if="vendor.tagline" class="mt-2 text-[1rem] font-semibold text-[#6d5542]">{{ vendor.tagline }}</p>
                <p class="mt-3 flex items-start gap-1.5 text-[0.95rem] leading-5 text-[#4b5563]">
                  <i class="ri-map-pin-fill mt-0.5 text-[#4b5563]"></i>
                  <span>{{ locationText }}</span>
                </p>
                <div class="mt-2 flex flex-wrap items-center gap-2 text-[0.9rem] leading-5">
                  <span v-if="vendor.available" class="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">
                    <i class="ri-checkbox-circle-line"></i>
                    Available
                  </span>
                  <span v-if="vendor.published" class="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1 font-semibold text-sky-700">
                    <i class="ri-shield-check-line"></i>
                    Published
                  </span>
                  <span v-if="vendor.category" class="inline-flex items-center gap-1 rounded-full bg-[#f5eee8] px-3 py-1 font-semibold text-[#6d5542]">
                    <i class="ri-price-tag-3-line"></i>
                    {{ vendor.category }}
                  </span>
                </div>
              </div>
              <div class="shrink-0 text-left md:text-right">
                <p class="text-[0.95rem] font-medium text-[#334155]">Talk to Vendor Manager</p>
                <p class="mt-1 text-[1rem] font-semibold text-[#334155]">
                  <i class="ri-phone-fill align-[-1px]"></i>
                  {{ vendorPhone || 'Contact unavailable' }}
                </p>
              </div>
            </div>

            <div v-if="catalogStyles.length" class="mt-4 grid gap-4 lg:grid-cols-2">
              <div v-if="catalogStyles.length">
                <p class="text-xs font-semibold uppercase tracking-[0.14em] text-[#b78969]">Styles</p>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span v-for="item in catalogStyles" :key="item" class="rounded-full bg-[#f5eee8] px-3 py-1.5 text-sm font-semibold text-[#6d5542]">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div class="min-w-0 space-y-4">
          <section v-if="hasVendorAbout" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">About</p>
            <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">About this service</h2>
            <RichTextContent class="mt-4 text-[1rem] leading-7 text-[#2f241b]" :html="vendor.about" fallback="No description added." />
          </section>

          <section v-if="serviceCatalogItems.length" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Catalog</p>
            <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Service catalog</h2>
            <div class="mt-4 grid gap-3 md:grid-cols-2">
              <div
                v-for="item in serviceCatalogItems"
                :key="item.key"
                class="rounded-[18px] border border-[#eadfd4] bg-[#fffdfa] p-4"
              >
                <p class="text-sm font-semibold text-ink">{{ item.label }}</p>
                <div v-if="item.values.length > 1" class="mt-3 flex flex-wrap gap-2">
                  <span
                    v-for="value in item.values"
                    :key="`${item.key}-${value}`"
                    class="rounded-full bg-[#f6efe8] px-3 py-1.5 text-sm font-semibold text-[#7a624e]"
                  >
                    {{ value }}
                  </span>
                </div>
                <p v-else class="mt-2 text-[1rem] font-semibold leading-6 text-[#6f5948]">{{ item.displayValue }}</p>
              </div>
            </div>
          </section>

          <section v-if="hasServicePolicyDetails" class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Policies</p>
            <h2 class="mt-2 font-serif text-[1.9rem] font-semibold text-ink">Booking terms</h2>
            <div class="mt-4 grid gap-3 md:grid-cols-2">
              <article v-for="item in servicePolicyItems" :key="item.label" class="rounded-[16px] border border-[#eadfd4] bg-[#fffdfa] p-4">
                <p class="text-sm font-semibold text-ink">{{ item.label }}</p>
                <RichTextContent
                  v-if="item.html"
                  class="mt-2 text-sm leading-6 text-[#6f5948]"
                  :html="item.value"
                />
                <p v-else class="mt-2 text-sm leading-6 text-[#6f5948]">{{ item.value }}</p>
              </article>
            </div>
          </section>

          <section class="rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Location</p>
              <h2 class="mt-2 font-serif text-[1.35rem] font-semibold leading-tight text-ink sm:text-[1.9rem]">{{ locationText }}</h2>
            </div>

            <div class="mt-4 overflow-hidden rounded-[20px] border border-[#d8e7ef] bg-[#eef7fb]">
              <iframe
                v-if="locationGoogleMapsEmbedUrl"
                :src="locationGoogleMapsEmbedUrl"
                class="h-[280px] w-full border-0"
                loading="lazy"
                referrerpolicy="no-referrer-when-downgrade"
                :title="`Google map for ${locationText}`"
              ></iframe>
              <div class="flex flex-col gap-3 border-t border-[#d8e7ef] bg-white px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                <div class="min-w-0">
                  <p class="font-semibold leading-6 text-[#2f241b]">{{ locationText }}</p>
                  <p class="text-sm leading-5 text-[#5b6c76]">{{ locationSubtitle }}</p>
                </div>
                <a
                  v-if="locationGoogleMapsLink"
                  :href="locationGoogleMapsLink"
                  target="_blank"
                  rel="noreferrer"
                  class="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#c99a22] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#b88817]"
                >
                  Open in Google Maps
                  <i class="ri-arrow-right-up-line"></i>
                </a>
              </div>
            </div>
          </section>
        </div>

        <aside class="min-w-0">
          <section class="sticky top-4 rounded-[24px] border border-[#eadfd4] bg-white p-4 shadow-sm sm:p-5">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#b78969]">Contact</p>
            <h2 class="mt-2 font-serif text-[1.55rem] font-semibold text-ink">Send enquiry</h2>
            <div class="mt-4 space-y-3 text-sm text-[#6f5948]">
              <p v-if="vendorPhone"><span class="font-semibold text-ink">Phone:</span> {{ vendorPhone }}</p>
              <p v-if="vendor.email"><span class="font-semibold text-ink">Email:</span> {{ vendor.email }}</p>
              <p v-if="vendor.website"><a class="font-semibold text-brand hover:text-brandDark" :href="vendor.website" target="_blank" rel="noopener">Open website</a></p>
              <p><span class="font-semibold text-ink">Location:</span> {{ locationText }}</p>
            </div>
            <div class="mt-4 grid grid-cols-2 gap-2 text-sm">
              <a v-if="phoneHref" :href="phoneHref" class="inline-flex items-center justify-center gap-2 rounded-xl border border-[#eadfd4] px-3 py-2 font-semibold text-ink transition hover:border-brand hover:text-brand">
                <i class="ri-phone-line"></i>
                Call
              </a>
              <a v-if="vendor.website" :href="vendor.website" target="_blank" rel="noopener" class="inline-flex items-center justify-center gap-2 rounded-xl border border-[#eadfd4] px-3 py-2 font-semibold text-ink transition hover:border-brand hover:text-brand">
                <i class="ri-global-line"></i>
                Website
              </a>
            </div>
            <form v-if="!enquirySubmitted" class="mt-5 space-y-3" @submit.prevent="submitServiceVendorEnquiry">
              <div v-if="!enquiryOtpSent" class="space-y-3">
              <div>
                <input v-model.trim="enquiryForm.name" type="text" :class="['service-enquiry-control', enquiryFieldErrors.name ? 'service-enquiry-control--error' : '']" placeholder="Full Name">
                <p v-if="enquiryFieldErrors.name" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.name }}</p>
              </div>
              <div>
                <input v-model.trim="enquiryForm.email" type="email" :class="['service-enquiry-control', enquiryFieldErrors.email ? 'service-enquiry-control--error' : '']" placeholder="Email">
                <p v-if="enquiryFieldErrors.email" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.email }}</p>
              </div>
              <div class="grid grid-cols-[88px_minmax(0,1fr)] gap-2">
                <input v-model.trim="enquiryForm.phoneCode" type="text" :class="['service-enquiry-control', enquiryFieldErrors.phoneCode ? 'service-enquiry-control--error' : '']" placeholder="+91">
                <input v-model.trim="enquiryForm.phone" type="tel" inputmode="numeric" maxlength="12" :class="['service-enquiry-control', enquiryFieldErrors.phone ? 'service-enquiry-control--error' : '']" placeholder="Mobile number" @input="limitPhoneDigits">
              </div>
              <p v-if="enquiryFieldErrors.phoneCode || enquiryFieldErrors.phone" class="mt-1 text-xs font-medium text-red-600">
                {{ enquiryFieldErrors.phoneCode || enquiryFieldErrors.phone }}
              </p>
              <div>
                <RichTextEditor
                  v-model="enquiryForm.details"
                  placeholder="Tell us what you need"
                  :show-heading="false"
                  :show-links="false"
                  :class="['service-enquiry-rich-editor', enquiryFieldErrors.details ? 'service-enquiry-rich-editor--error' : '']"
                />
                <p v-if="enquiryFieldErrors.details" class="mt-1 text-xs font-medium text-red-600">{{ enquiryFieldErrors.details }}</p>
              </div>
              </div>
              <div v-else class="rounded-2xl border border-[#eadfd4] bg-[#fff7ef] p-4">
                <p class="text-xs font-semibold uppercase tracking-wide text-[#6f5948]">Email OTP</p>
                <p class="mt-1 text-xs text-[#7a6657]">Enter the 4 digit code sent to {{ enquiryOtpEmail }}.</p>
                <div class="mt-3 grid grid-cols-4 gap-2">
                  <input
                    v-for="(_, index) in enquiryOtpDigits"
                    :key="`service-otp-${index}`"
                    :ref="(el) => setServiceOtpInputRef(el, index)"
                    v-model="enquiryOtpDigits[index]"
                    type="text"
                    inputmode="numeric"
                    maxlength="1"
                    class="service-enquiry-otp"
                    :class="{ 'service-enquiry-control--error': enquiryFieldErrors.otp }"
                    @input="handleServiceOtpInput(index)"
                    @keydown="handleServiceOtpKeydown($event, index)"
                    @paste="handleServiceOtpPaste"
                  >
                </div>
                <p v-if="enquiryFieldErrors.otp" class="mt-2 text-xs font-medium text-red-600">{{ enquiryFieldErrors.otp }}</p>
              </div>
              <p v-if="enquiryError" class="rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                {{ enquiryError }}
              </p>
              <p v-if="enquirySuccess" class="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                {{ enquirySuccess }}
              </p>
              <button type="submit" class="w-full rounded-none bg-[#ff310f] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#dd270b] disabled:cursor-not-allowed disabled:opacity-70" :disabled="enquiryLoading">
                {{ enquiryLoading ? (enquiryOtpSent ? 'Verifying...' : 'Checking...') : (enquiryOtpSent ? 'Verify & Submit Enquiry' : 'Check Availability') }}
              </button>
            </form>
            <div v-else class="mt-5 space-y-3">
              <div class="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-medium text-emerald-800">
                {{ enquirySuccess || 'Enquiry sent successfully.' }}
              </div>
              <div v-if="submittedEnquiry" class="rounded-2xl border border-[#eadfd4] bg-[#fffaf5] p-4 text-sm text-[#6f5948]">
                <p class="font-semibold text-ink">{{ submittedEnquiry.name }}</p>
                <p class="mt-1">{{ submittedEnquiry.phoneCode }} {{ submittedEnquiry.phone }}</p>
                <p class="mt-1">{{ submittedEnquiry.email }}</p>
                <div class="mt-3 border-t border-[#eadfd4] pt-3">
                  <span class="font-semibold text-ink">Requirements:</span>
                  <RichTextContent class="mt-1 text-sm leading-6 text-[#6f5948]" :html="submittedEnquiry.details" fallback="N/A" />
                </div>
              </div>
              <button type="button" class="w-full rounded-none border border-[#cfcfcf] bg-white px-4 py-3 text-sm font-semibold text-[#1f2a44] transition hover:border-brand hover:text-brand" @click="resetServiceVendorEnquiry">
                Send another enquiry
              </button>
            </div>
          </section>
        </aside>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import lightGallery from 'lightgallery'
import lgThumbnail from 'lightgallery/plugins/thumbnail'
import lgZoom from 'lightgallery/plugins/zoom'
import RichTextContent from '@/components/common/RichTextContent.vue'
import RichTextEditor from '@/components/common/RichTextEditor.vue'
import userApi from '@/services/user/api'

const route = useRoute()
const loading = ref(false)
const error = ref('')
const vendor = ref({
  id: '',
  title: '',
  category: '',
  about: '',
  city: '',
  state: '',
  phoneCode: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  tagline: '',
  metaDescription: '',
  latitude: '',
  longitude: '',
  available: false,
  published: false,
  highlights: [],
  catalogThemes: [],
  catalogStyles: [],
  serviceCatalog: [],
  photos: []
})
const policy = ref({ cancelTerms: '', depositPercent: '' })
const enquiryLoading = ref(false)
const enquiryError = ref('')
const enquirySuccess = ref('')
const enquirySubmitted = ref(false)
const submittedEnquiry = ref(null)
const enquiryFieldErrors = ref({})
const enquiryOtpSent = ref(false)
const enquiryOtpEmail = ref('')
const serviceVendorEnquiryId = ref('')
const enquiryOtpDigits = ref(['', '', '', ''])
const serviceOtpInputRefs = ref([])
const vendorGalleryLightbox = ref(null)
const enquiryForm = ref({
  name: '',
  email: '',
  phoneCode: '+91',
  phone: '',
  details: ''
})

const placeholderImages = [
  'https://dummyimage.com/900x675/f5efe8/2f241b&text=Service',
  'https://dummyimage.com/900x675/fff7e8/80550d&text=Portfolio',
  'https://dummyimage.com/900x675/efe7ff/251833&text=Vendor',
  'https://dummyimage.com/900x675/eef6ff/1d4f8f&text=Work'
]

const serviceVendorId = computed(() => String(route.params.serviceVendorId || route.params.vendorId || route.params.id || '').trim())
const vendorId = computed(() => String(route.params.vendorId || route.query.vendor_id || route.query.vendorId || route.params.businessId || route.query.businessId || '').trim())
const locationText = computed(() => vendor.value.address || [vendor.value.city, vendor.value.state].filter(Boolean).join(', ') || 'Location unavailable')
const locationSubtitle = computed(() => [vendor.value.city, vendor.value.state].filter(Boolean).join(', ') || vendor.value.address || 'Location not added yet.')
const vendorPhone = computed(() => vendor.value.phone ? `${vendor.value.phoneCode || ''} ${vendor.value.phone}`.trim() : '')
const phoneHref = computed(() => vendorPhone.value ? `tel:${vendorPhone.value.replace(/[^\d+]/g, '')}` : '')
const serviceCatalogItems = computed(() => normalizeServiceCatalog(vendor.value.serviceCatalog))
const catalogStyles = computed(() => splitToArray(vendor.value.catalogStyles || getServiceCatalogValue(['styles', 'style', 'service_styles'])))
const galleryImages = computed(() => normalizePhotos(vendor.value.photos).filter(Boolean))
const lightboxGalleryImages = computed(() =>
  galleryImages.value.map((src, index) => ({
    id: `gallery-${index}-${src}`,
    src
  }))
)
const locationGoogleMapsLink = computed(() => {
  const coordinateQuery = vendor.value.latitude && vendor.value.longitude ? `${vendor.value.latitude},${vendor.value.longitude}` : ''
  const addressQuery = [vendor.value.address, vendor.value.city, vendor.value.state].filter(Boolean).join(', ')
  return buildGoogleMapsLink(coordinateQuery || addressQuery)
})
const locationGoogleMapsEmbedUrl = computed(() => {
  const coordinateQuery = vendor.value.latitude && vendor.value.longitude ? `${vendor.value.latitude},${vendor.value.longitude}` : ''
  const addressQuery = [vendor.value.address, vendor.value.city, vendor.value.state].filter(Boolean).join(', ')
  const query = coordinateQuery || addressQuery
  return query ? `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed` : ''
})
const hasVendorAbout = computed(() => Boolean(stripHtml(vendor.value.about).trim()))
const heroGallery = computed(() => {
  const images = lightboxGalleryImages.value.length
    ? lightboxGalleryImages.value
    : placeholderImages.map((src, index) => ({
        id: `placeholder-${index}`,
        src,
        isPlaceholder: true
      }))
  const tiles = images.slice(0, 4).map((image) => ({ ...image }))
  const remaining = images.length - 4
  if (remaining > 0 && tiles[3]) tiles[3].overlayText = `+${remaining}`
  return tiles
})
const placeholderTextValues = new Set(['n/a', 'na', 'none', 'null', 'undefined', '-'])

const hasMeaningfulText = (value) => {
  const normalized = stripHtml(value).replace(/\s+/g, ' ').trim()
  return Boolean(normalized && !placeholderTextValues.has(normalized.toLowerCase()))
}

const hasPositiveNumericValue = (value) => {
  if (String(value ?? '').trim() === '') return false
  const numeric = Number(String(value || '').replace(/[^\d.]/g, ''))
  return Number.isFinite(numeric) && numeric > 0
}

const servicePolicyItems = computed(() => [
  hasPositiveNumericValue(policy.value.depositPercent)
    ? {
        label: 'Deposit',
        value: `${policy.value.depositPercent}% to confirm booking`
      }
    : null,
  hasMeaningfulText(policy.value.cancelTerms)
    ? {
        label: 'Cancellation terms',
        value: policy.value.cancelTerms,
        html: true
      }
    : null
].filter(Boolean))

const hasServicePolicyDetails = computed(() => servicePolicyItems.value.length > 0)

const splitToArray = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item?.name || item?.label || item?.title || item?.src || item?.url || item || '').trim()).filter(Boolean)
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean)
}

const formatCatalogLabel = (value) => {
  const label = String(value || '').replace(/_/g, ' ').trim()
  if (!label) return 'Service detail'
  return label
}

const normalizeCatalogValue = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item?.name || item?.label || item?.title || item?.value || item || '').trim()).filter(Boolean)
  if (value && typeof value === 'object') return splitToArray(value?.name || value?.label || value?.title || value?.value)
  return splitToArray(value)
}

const hasCatalogValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  return normalized && !['n/a', 'na', 'null', 'undefined'].includes(normalized)
}

const normalizeServiceCatalog = (value) => {
  const items = Array.isArray(value)
    ? value
    : Object.entries(value && typeof value === 'object' ? value : {}).map(([param, item]) => ({
        param,
        name: item?.name || item?.label || param,
        value: item?.value ?? item
      }))

  return items
    .map((item, index) => {
      const label = formatCatalogLabel(item?.name || item?.field || item?.label || item?.param)
      const values = normalizeCatalogValue(item?.value ?? item?.values ?? item?.selected ?? '').filter(hasCatalogValue)
      const displayValue = values.join(', ')
      return {
        key: String(item?.id || item?.param || `${label}-${index}`),
        param: String(item?.param || '').trim(),
        label,
        values,
        displayValue
      }
    })
    .filter((item) => item.label && item.values.length)
}

const getServiceCatalogValue = (params = []) => {
  const normalizedParams = params.map((param) => String(param || '').toLowerCase())
  const match = serviceCatalogItems.value.find((item) => normalizedParams.includes(String(item.param || '').toLowerCase()))
  return match?.displayValue || ''
}

const normalizeExternalUrl = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return raw
  if (/^www\./i.test(raw)) return `https://${raw}`
  return ''
}

const buildGoogleMapsLink = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return normalizeExternalUrl(raw) || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(raw)}`
}

const normalizePhotos = (value) => {
  if (!Array.isArray(value)) return splitToArray(value)
  return [...value]
    .sort((a, b) => Number(b?.is_cover || 0) - Number(a?.is_cover || 0))
    .map((item) => String(item?.src || item?.url || item?.image || item?.photo || item?.simg_img || item || '').trim())
    .filter(Boolean)
}

const normalizeVendor = (item = {}) => {
  const catalog = item?.catalog || item?.service_catalog || item
  const website = item?.website || item?.website_link || item?.web_url || ''
  return {
    id: String(item?.service_vendor_id || item?.vendor_id || item?.id || item?.s_id || serviceVendorId.value || '').trim(),
    title: item?.brand_name || item?.brandName || item?.business_name || item?.s_business || item?.s_name || item?.name || item?.title || 'Service vendor',
    category: item?.cat_name || item?.category_name || item?.service_vendor_category || item?.businesscategory || item?.s_category || item?.category || '',
    tagline: item?.tagline || '',
    about: item?.about || item?.description || item?.s_desc || item?.details || '',
    metaDescription: item?.meta_description || item?.metaDescription || '',
    city: item?.city || item?.s_city || '',
    state: item?.state || item?.s_state || '',
    address: item?.address || item?.s_address || item?.s_address1 || '',
    latitude: item?.latitude || item?.lat || '',
    longitude: item?.longitude || item?.lng || '',
    phoneCode: item?.phone_code || item?.phoneCode || item?.s_phone_code || '',
    phone: item?.phone || item?.s_phone || '',
    email: item?.email || item?.s_email || '',
    website: normalizeWebsite(website),
    available: item?.venue_availability === true || item?.availability === true || String(item?.availability || '').toLowerCase() === 'available',
    published: String(item?.published ?? item?.status ?? '') === '1',
    highlights: item?.highlights || item?.highlight || item?.tags || [],
    catalogThemes: catalog?.themes || item?.catalogThemes || item?.catalog_themes || item?.act_types || item?.shoot_types || item?.cuisines || [],
    catalogStyles: catalog?.styles || item?.catalogStyles || item?.catalog_styles || item?.music_genres || item?.service_styles || [],
    serviceCatalog: item?.service_catalog || item?.serviceCatalog || item?.catalog_info || item?.catalogInfo || [],
    photos: item?.gallery_info || item?.galleryInfo || item?.photos || item?.gallery || item?.images || item?.s_images || item?.service_vendor_photos || item?.portfolio || []
  }
}

const extractArrayPayload = (data, keys = []) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key]
  }
  return []
}

const extractDetailPayload = (data) => {
  const payload = data?.data || data || {}
  if (Array.isArray(payload)) return payload[0] || {}
  const nestedKeys = ['service_vendor', 'serviceVendor', 'vendor', 'details', 'detail', 'item']
  for (const key of nestedKeys) {
    const value = payload?.[key]
    if (Array.isArray(value)) return value[0] || {}
    if (value && typeof value === 'object') return value
  }
  const listItem = extractArrayPayload(payload, ['service_vendors', 'vendors', 'items', 'list', 'service_vendor_list'])[0]
  return listItem || payload
}

const extractPolicy = (item = {}) => {
  const source = item?.policy || item?.policies || item?.service_vendor_policy || item
  const firstPolicy = Array.isArray(source) ? source[0] : source
  return {
    cancelTerms: firstPolicy?.cancel_terms || firstPolicy?.cancellationTerms || item?.cancel_terms || item?.cancellationTerms || '',
    depositPercent: firstPolicy?.deposit_percent || firstPolicy?.depositPercent || item?.deposit_percent || item?.depositPercent || ''
  }
}

const stripHtml = (value) => {
  if (typeof document === 'undefined') return String(value || '').replace(/<[^>]+>/g, ' ')
  const container = document.createElement('div')
  container.innerHTML = String(value || '')
  return container.textContent || container.innerText || ''
}

const normalizeWebsite = (value) => {
  const website = String(value || '').trim()
  if (!website) return ''
  return /^https?:\/\//i.test(website) ? website : `https://${website}`
}

const destroyVendorGallery = () => {
  if (vendorGalleryLightbox.value) {
    vendorGalleryLightbox.value.destroy(true)
    vendorGalleryLightbox.value = null
  }
}

const openVendorGallery = (startIndex = 0) => {
  if (!lightboxGalleryImages.value.length) return
  destroyVendorGallery()
  const safeIndex = Math.max(0, Math.min(startIndex, lightboxGalleryImages.value.length - 1))
  vendorGalleryLightbox.value = lightGallery(document.createElement('div'), {
    dynamic: true,
    dynamicEl: lightboxGalleryImages.value.map((image) => ({
      src: image.src,
      thumb: image.src
    })),
    index: safeIndex,
    plugins: [lgThumbnail, lgZoom],
    download: false,
    actualSize: false
  })
  vendorGalleryLightbox.value.openGallery(safeIndex)
}

const openVendorGalleryById = (image) => {
  if (image?.isPlaceholder) return
  const imageId = String(image?.id || '')
  const startIndex = lightboxGalleryImages.value.findIndex((item) => String(item.id || '') === imageId)
  openVendorGallery(startIndex >= 0 ? startIndex : 0)
}

const validateEnquiryForm = () => {
  const nextErrors = {}
  const email = String(enquiryForm.value.email || '').trim()
  const phoneCode = String(enquiryForm.value.phoneCode || '').trim()
  const phone = String(enquiryForm.value.phone || '').replace(/\D/g, '')

  if (!String(enquiryForm.value.name || '').trim()) nextErrors.name = 'Please enter your full name.'
  if (!email) nextErrors.email = 'Please enter your email.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) nextErrors.email = 'Please enter a valid email.'
  if (!phoneCode) nextErrors.phoneCode = 'Please enter phone code.'
  if (!phone) nextErrors.phone = 'Please enter your mobile number.'
  else if (phone.length < 7) nextErrors.phone = 'Please enter a valid mobile number.'
  if (!stripHtml(enquiryForm.value.details).trim()) nextErrors.details = 'Please add your requirements.'

  enquiryFieldErrors.value = nextErrors
  return !Object.keys(nextErrors).length
}

const limitPhoneDigits = () => {
  enquiryForm.value.phone = String(enquiryForm.value.phone || '').replace(/\D/g, '').slice(0, 12)
}

const enquiryOtpCode = computed(() => enquiryOtpDigits.value.join(''))

const resetServiceVendorEnquiryOtp = () => {
  enquiryOtpSent.value = false
  enquiryOtpEmail.value = ''
  serviceVendorEnquiryId.value = ''
  enquiryOtpDigits.value = ['', '', '', '']
  serviceOtpInputRefs.value = []
}

const setServiceOtpInputRef = (el, index) => {
  if (el) serviceOtpInputRefs.value[index] = el
}

const focusServiceOtpInput = async (index) => {
  await nextTick()
  serviceOtpInputRefs.value[index]?.focus?.()
}

const handleServiceOtpInput = (index) => {
  enquiryOtpDigits.value[index] = String(enquiryOtpDigits.value[index] || '').replace(/\D/g, '').slice(0, 1)
  enquiryFieldErrors.value = { ...enquiryFieldErrors.value, otp: '' }
  if (enquiryOtpDigits.value[index] && index < enquiryOtpDigits.value.length - 1) {
    focusServiceOtpInput(index + 1)
  }
}

const handleServiceOtpKeydown = (event, index) => {
  if (event.key === 'Backspace' && !enquiryOtpDigits.value[index] && index > 0) {
    focusServiceOtpInput(index - 1)
  }
}

const handleServiceOtpPaste = (event) => {
  event.preventDefault()
  const digits = String(event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 4).split('')
  enquiryOtpDigits.value = ['', '', '', ''].map((_, index) => digits[index] || '')
  focusServiceOtpInput(Math.min(digits.length, 3))
}

const verifyServiceVendorEnquiryOtp = async () => {
  const otp = enquiryOtpCode.value
  if (!/^\d{4}$/.test(otp)) {
    enquiryFieldErrors.value = { ...enquiryFieldErrors.value, otp: 'Enter the 4 digit OTP.' }
    return false
  }
  const formData = new FormData()
  formData.append('enquiry_id', serviceVendorEnquiryId.value)
  formData.append('otp', otp)
  const { data } = await userApi.verifyServiceVendorEnquiry(formData)
  const isError = data?.status === 0 || data?.status === false || data?.status === 'error' || data?.success === false
  if (isError) throw new Error(data?.msg || data?.message || 'Invalid OTP.')
  return true
}

const resetServiceVendorEnquiry = () => {
  enquiryForm.value = {
    name: '',
    email: '',
    phoneCode: '+91',
    phone: '',
    details: ''
  }
  enquiryError.value = ''
  enquirySuccess.value = ''
  enquirySubmitted.value = false
  submittedEnquiry.value = null
  enquiryFieldErrors.value = {}
  resetServiceVendorEnquiryOtp()
}

const createServiceVendorEnquiry = async () => {
  const formData = new FormData()
  formData.append('service_vendor_id', vendor.value.id || serviceVendorId.value)
  formData.append('name', enquiryForm.value.name)
  formData.append('email', enquiryForm.value.email)
  formData.append('phone_code', enquiryForm.value.phoneCode || '+91')
  formData.append('phone', enquiryForm.value.phone)
  formData.append('details', enquiryForm.value.details)
  const { data } = await userApi.sendServiceVendorEnquiry(formData)
  const ok = data?.status === 'ok' || data?.status === true || data?.success === true || data?.status === 'success'
  if (!ok && data?.status === false) throw new Error(data?.msg || data?.message || 'Unable to send enquiry.')
  const enquiryId = data?.enquiry_id || data?.data?.enquiry_id || data?.data?.id || data?.id || ''
  if (!enquiryId) throw new Error(data?.msg || data?.message || 'Enquiry ID was not returned.')
  serviceVendorEnquiryId.value = String(enquiryId)
  enquiryOtpSent.value = true
  enquiryOtpEmail.value = enquiryForm.value.email
  enquiryOtpDigits.value = ['', '', '', '']
  enquirySuccess.value = data?.message || data?.msg || 'OTP sent to your email.'
  await focusServiceOtpInput(0)
}

const submitServiceVendorEnquiry = async () => {
  enquiryError.value = ''
  enquirySuccess.value = ''
  if (!validateEnquiryForm()) {
    enquiryError.value = 'Please fix the highlighted fields before sending your enquiry.'
    return
  }

  enquiryLoading.value = true
  try {
    if (!enquiryOtpSent.value || !serviceVendorEnquiryId.value) {
      await createServiceVendorEnquiry()
      return
    }
    const verified = await verifyServiceVendorEnquiryOtp()
    if (!verified) return
    submittedEnquiry.value = { ...enquiryForm.value }
    enquirySuccess.value = 'Enquiry submitted successfully.'
    enquirySubmitted.value = true
    enquiryFieldErrors.value = {}
    resetServiceVendorEnquiryOtp()
  } catch (err) {
    enquiryError.value = err?.response?.data?.msg || err?.response?.data?.message || err?.message || 'Unable to verify OTP.'
  } finally {
    enquiryLoading.value = false
  }
}

const loadVendorDetailInvite = async () => {
  const formData = new FormData()
  formData.append('service_vendor_id', serviceVendorId.value)
  formData.append('vendor_id', vendorId.value)
  const { data } = await userApi.getServiceVendorDetailInvite(formData)
  return extractDetailPayload(data)
}

const loadServiceVendor = async () => {
  loading.value = true
  error.value = ''
  try {
    const detail = await loadVendorDetailInvite()
    if (!Object.keys(detail).length) throw new Error('Service vendor not found.')
    vendor.value = normalizeVendor(detail)
    policy.value = extractPolicy(detail)
  } catch (err) {
    error.value = err?.message || 'Failed to load service vendor details.'
  } finally {
    loading.value = false
  }
}

watch(() => route.fullPath, loadServiceVendor)
onMounted(loadServiceVendor)
onBeforeUnmount(destroyVendorGallery)
</script>

<style scoped>
.service-enquiry-control {
  width: 100%;
  min-height: 46px;
  border: 1px solid #eadfd4;
  border-radius: 14px;
  background: #fffdfa;
  padding: 0 14px;
  font-size: 14px;
  font-weight: 600;
  color: #1f1208;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.service-enquiry-control::placeholder {
  color: #a58c78;
}

.service-enquiry-control:focus {
  border-color: #ff310f;
  box-shadow: 0 0 0 3px rgba(255, 49, 15, 0.1);
}

.service-enquiry-control--error {
  border-color: #dc2626;
}

.service-enquiry-otp {
  min-height: 46px;
  border: 1px solid #eadfd4;
  border-radius: 14px;
  background: #fffdfa;
  text-align: center;
  font-size: 18px;
  font-weight: 700;
  color: #1f1208;
  outline: none;
}

.service-enquiry-otp:focus {
  border-color: #ff310f;
  box-shadow: 0 0 0 3px rgba(255, 49, 15, 0.1);
}

.service-enquiry-rich-editor {
  border: 1px solid #eadfd4;
  border-radius: 14px;
  overflow: hidden;
  background: #fffdfa;
}

.service-enquiry-rich-editor--error {
  border-color: #dc2626;
}

.service-enquiry-rich-editor :deep(.rich-text-editor__toolbar) {
  border-bottom-color: #eadfd4;
  background: #fff7ef;
}

.service-enquiry-rich-editor :deep(.rich-text-editor__surface) {
  min-height: 120px;
  padding: 12px 14px;
  font-size: 14px;
  color: #1f1208;
}
</style>
